// src/pages/Home.js
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Target, Users, Lightbulb, TrendingUp, BookOpen } from 'lucide-react'
import ServiceCard from '../compenents/ServiceCard'
import Button from '../compenents/Button'
import Hero from '../compenents/Hero'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'
import About from './About'

const ZigzagBorder = ({ position = 'top', color = 'white' }) => {
  const height = 40
  const zigzagPath = `M0,${height} L20,0 L40,${height} L60,0 L80,${height} L100,0 L120,${height} L140,0 L160,${height} L180,0 L200,${height} L220,0 L240,${height} L260,0 L280,${height} L300,0 L320,${height} L340,0 L360,${height} L380,0 L400,${height} L420,0 L440,${height} L460,0 L480,${height} L500,0 L520,${height} L540,0 L560,${height} L580,0 L600,${height} L620,0 L640,${height} L660,0 L680,${height} L700,0 L720,${height} L740,0 L760,${height} L780,0 L800,${height} L820,0 L840,${height} L860,0 L880,${height} L900,0 L920,${height} L940,0 L960,${height} L980,0 L1000,${height} L1000,${height + 10} L0,${height + 10} Z`
  
  const positionStyles = position === 'top' 
    ? { top: -1, transform: 'scaleY(-1)' }
    : { bottom: -1 }
  
  return (
    <div className="absolute left-0 w-full overflow-hidden pointer-events-none z-20" style={positionStyles}>
      <svg className="w-full" height={height + 10} viewBox={`0 0 1000 ${height + 10}`} preserveAspectRatio="none">
        <path d={zigzagPath} fill={color} />
      </svg>
    </div>
  )
}

const Home = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const services = [
    {
      icon: Target,
      title: t('home.service1Title'),
      description: t('home.service1Desc'),
      link: '/conseil'
    },
    {
      icon: Users,
      title: t('home.service2Title'),
      description: t('home.service2Desc'),
      link: '/accompagnement'
    },
    {
      icon: Lightbulb,
      title: t('home.service3Title'),
      description: t('home.service3Desc'),
      link: '/ai-solutions'
    },
    {
      icon: TrendingUp,
      title: t('home.service4Title'),
      description: t('home.service4Desc'),
      link: '/partnerships'
    },
     {
    icon: BookOpen, // Icône pour Formation
    title: t('services.training.feature1'),
    description: t('services.training.description'),
    link: '/training'
  },
  {
    icon: Users, // Icône pour Recrutement
    title: t('services.recruitment.feature1'),
    description: t('services.recruitment.description'),
    link: '/recruitment'
  }
  ]

  const features = [
    t('home.ctaFeature1'),
    t('home.ctaFeature2'),
    t('home.ctaFeature3')
  ]

  return (
    <div className="pt-26">
      {/* Section Hero */}
       <div className="hero-section">
        <Hero />
      </div>

      {/* Section About */}
      <About />

      {/* Section Services */}
      <section className="relative py-24 bg-gray-50">
        <ZigzagBorder position="top" color="#f9fafb" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-4">
              {t('home.expertiseTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.expertiseSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} index={index} />
            ))}
          </div>
        </div>
        
        <ZigzagBorder position="bottom" color="#ffffff" />
      </section>

      {/* Section CTA */}
      <section className="relative py-20 bg-white">
        <ZigzagBorder position="top" color="#ffffff" />
        
        {/* Arrière-plan animé */}
        <div className="absolute inset-0 opacity-5 overflow-hidden pointer-events-none">
          <motion.div 
            animate={{ 
              rotate: 360,
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -top-32 -left-32 w-96 h-96 bg-blue-500 rounded-full blur-3xl"
          />
          <motion.div 
            animate={{ 
              rotate: -360,
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -bottom-32 -right-32 w-96 h-96 bg-orange-500 rounded-full blur-3xl"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-heading font-bold text-gray-900 mb-6">
                {t('home.ctaTitle')}
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('home.ctaDescription')}
              </p>
              <ul className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center mt-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="ml-3 text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button variant="primary" size="lg">
                  {t('home.ctaButton')}
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/suport_client.webp"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold mb-1">24/7</p>
                <p className="text-sm">{t('home.supportLabel') || "Support Client"}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    
    </div>
  )
}

export default Home